import { useEffect, useRef, useState } from "react";
import { requestMagicLink } from "plg/api/plgAuth";
import { trackPlg, trackSignupBlocked } from "plg/analytics/events";
import { emailDomain } from "plg/analytics/slugs";
import { getHumanPass } from "plg/auth/humanPass";
import type { SignInError } from "plg/screens/Signup/authStartResult";
import "./MagicLink.scss";

type FormError = "tooMany" | "disposable" | "plusEmail" | "businessEmail" | "generic";

/** [title, body] as the prototype's .ob-note; a null title is a one-line note. */
const MESSAGES: Record<FormError, [string | null, string]> = {
  tooMany: [null, "Too many attempts. Try again later."],
  disposable: ["That address will not work", "We cannot reach that inbox. Try a different email."],
  plusEmail: ["That address will not work", "Use your email address without a \"+\" tag."],
  // Gmail, Outlook, Yahoo...: refused before a link is sent (product 2026-10-26)
  businessEmail: ["We need a business email", "Personal addresses such as Gmail, Outlook or Yahoo cannot be used. Try your work email."],
  generic: [null, "Something went wrong. Please try again."],
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface Props {
  disabled?: boolean;
  /** Put the cursor in the email field (back from "Check your inbox" via "Use a different address"). */
  autoFocus?: boolean;
  /** Errors that replace the whole card (COUNTRY_NOT_SUPPORTED). */
  onBlocked: (error: SignInError) => void;
  /** The human pass is missing or expired: show the Cloudflare gate again. */
  onHumanRequired: () => void;
  /** The link is on its way: the page replaces the whole card with "Check your inbox" (prototype renderMagic). */
  onSent?: (email: string) => void;
  /** Filled in on the way back from "Check your inbox", so a typo can be fixed. */
  initialEmail?: string;
}

/** "or use your email": work email + "Email me a sign-in link". The human check already ran (v2.2 gate). */
function MagicLinkForm({ disabled, autoFocus, onBlocked, onHumanRequired, onSent, initialEmail = "" }: Props) {
  const [email, setEmail] = useState(initialEmail);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<FormError | null>(null);

  const address = email.trim().toLowerCase();
  // The button stays disabled until the address looks like an email, so there's no error box for it.
  const valid = EMAIL_RE.test(address);

  const send = async () => {
    if (!valid || sending) return;
    const humanPass = getHumanPass();
    if (!humanPass) {
      onHumanRequired();
      return;
    }
    setSending(true);
    setError(null);
    const res = await requestMagicLink({ email: address, humanPass });
    setSending(false);
    if (res.ok === true) {
      if (onSent) onSent(address);
      else setSentTo(address);
      return;
    }
    trackSignupBlocked(res.code, emailDomain(address));
    // The page's SignInErrorNote tracks error_shown for ACCOUNT_EXISTS / ADMIN_ACCOUNT_EXISTS itself.
    const noted = res.code === "ACCOUNT_EXISTS" || res.code === "ADMIN_ACCOUNT_EXISTS";
    if (res.code !== "COUNTRY_NOT_SUPPORTED" && res.code !== "HUMAN_CHECK_REQUIRED" && !noted) {
      trackPlg("error_shown", { error_type: res.code || (res.status === 429 ? "TOO_MANY_ATTEMPTS" : "generic"), screen: "sign_in" });
    }
    if (res.code === "COUNTRY_NOT_SUPPORTED") onBlocked("country");
    else if (res.code === "ACCOUNT_EXISTS") onBlocked("exists");
    else if (res.code === "ADMIN_ACCOUNT_EXISTS") onBlocked("adminExists");
    else if (res.code === "HUMAN_CHECK_REQUIRED") onHumanRequired();
    else if (res.code === "TOO_MANY_ATTEMPTS" || res.status === 429) setError("tooMany");
    else if (res.code === "DISPOSABLE_EMAIL") setError("disposable");
    else if (res.code === "PLUS_EMAIL_NOT_ALLOWED") setError("plusEmail");
    else if (res.code === "BUSINESS_EMAIL_REQUIRED") setError("businessEmail");
    else setError("generic");
  };

  const startOver = () => {
    setSentTo(null);
    setEmail("");
    setError(null);
  };

  if (sentTo) {
    return (
      <div className="plg-magic">
        <div className="plg-magic__or">or use your email</div>
        <div className="plg-magic__sent" role="status">
          <b>Check your inbox</b>
          <span>We sent a sign-in link to {sentTo}. The link expires in 15 minutes and works in this browser.</span>
          <button type="button" className="plg-magic__link" onClick={startOver}>
            Use a different address
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="plg-magic">
      <div className="plg-magic__or">or use your email</div>
      <label className="plg-magic__label" htmlFor="plg-magic-email">
        Work email
      </label>
      <input
        ref={inputRef}
        id="plg-magic-email"
        className={`plg-magic__input${error === "disposable" || error === "plusEmail" ? " plg-magic__input--error" : ""}`}
        type="email"
        autoComplete="email"
        placeholder="you@yourbusiness.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") send();
        }}
      />
      {error && (
        <div className="plg-signup__note" role="alert">
          {MESSAGES[error][0] && <b>{MESSAGES[error][0]}</b>}
          {MESSAGES[error][1]}
        </div>
      )}
      <button type="button" className="plg-magic__submit" onClick={send} disabled={disabled || sending || !valid}>
        {sending ? "Sending…" : "Email me a sign-in link"}
      </button>
    </div>
  );
}

export default MagicLinkForm;
